import { HttpClient } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import { IItemBase } from '../../items.interface';
import { IEditItem, IEscalateOptionBoard } from './item-escalete.interface';

export interface IItemEscalateState {
  boardsLoading: ELoadStatus;
  boards: IEscalateOptionBoard[];
  editItemLoading: ELoadStatus;
}

@Injectable()
export class ItemEscalateStore extends ComponentStore<IItemEscalateState> {
  public readonly boards: Signal<IEscalateOptionBoard[]> = this.selectSignal(
    (state: IItemEscalateState) => state.boards || [],
  );

  readonly loadBoards = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((itemId: number) => {
        this.patchState({ boards: [], boardsLoading: ELoadStatus.loading });

        return this.getEscalateOptions(itemId).pipe(
          tapResponse(
            (output) =>
              this.patchState({
                boards: output.data,
                boardsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading boards', error),
          ),
        );
      }),
    ),
  );

  readonly editItemEscalatedBoardId = this.effect((trigger$: Observable<IEditItem>) =>
    trigger$.pipe(
      switchMap((editItem: IEditItem) => {
        this.patchState({ editItemLoading: ELoadStatus.loading });

        return this.editItem(editItem).pipe(
          tapResponse(
            () =>
              this.patchState({
                editItemLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      boards: [],
      boardsLoading: ELoadStatus.initial,
      editItemLoading: ELoadStatus.initial,
    });
  }

  private getEscalateOptions(itemId: number): Observable<IGetManyResponse<IEscalateOptionBoard>> {
    return this.http.get<IGetManyResponse<IEscalateOptionBoard>>(`items/${itemId}/escalate-options`);
  }

  private editItem(item: IEditItem): Observable<IBaseResponse<IItemBase>> {
    return this.http.patch<IBaseResponse<IItemBase>>(`items/${item.id}`, {
      assigneeId: null,
      escalatedBoardId: item.escalatedBoardId,
    });
  }
}
